import { useCallback, useEffect, useState } from 'react';
import { ChevronDown, CreditCard, Plus, ShieldCheck } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import { useAuth } from '../../../contexts/AuthContext';
import { useMediaQuery } from '../../../hooks/useMediaQuery';
import LoadingSpinner from '../../common/LoadingSpinner';

function CompactStat({ label, value, valueClassName = '' }) {
    return (
        <div className="rounded-lg border border-gray-100 bg-white px-2.5 py-2 dark:border-white/10 dark:bg-gray-800/80">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-gray-500 dark:text-white/50">{label}</p>
            <p className={`mt-0.5 truncate text-sm font-medium text-gray-900 dark:text-white ${valueClassName}`}>{value || '-'}</p>
        </div>
    );
}

function Panel({ title, icon: Icon, action, children, alert }) {
    return (
        <section className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-white/10 dark:bg-gray-900/40">
            <div className="flex items-center justify-between gap-2 border-b border-gray-100 bg-gray-50/90 px-3 py-2 dark:border-white/5 dark:bg-gray-900/60">
                <h4 className="flex items-center gap-1.5 text-sm font-semibold text-gray-900 dark:text-white">
                    {Icon ? <Icon size={15} className="text-indigo-600 dark:text-indigo-400" aria-hidden /> : null}
                    {title}
                </h4>
                {action}
            </div>
            {alert ? <div className="border-b border-amber-200 bg-amber-50 px-3 py-2 text-xs font-medium text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/30 dark:text-amber-100">{alert}</div> : null}
            <div className="p-3">{children}</div>
        </section>
    );
}

function CollapsibleHistory({ title, count, children, defaultOpen = false }) {
    const [open, setOpen] = useState(defaultOpen);
    if (!count) return null;
    return (
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-white/10">
            <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                className="flex w-full items-center justify-between gap-2 bg-gray-50/90 px-3 py-2 text-left text-sm font-medium text-gray-800 dark:bg-gray-900/60 dark:text-white"
            >
                <span>{title} ({count})</span>
                <ChevronDown size={16} className={`shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} aria-hidden />
            </button>
            {open ? <div className="divide-y divide-gray-100 border-t border-gray-100 dark:divide-white/5 dark:border-white/5">{children}</div> : null}
        </div>
    );
}

function isSubscriptionExpired(subscription) {
    if (!subscription) return false;
    if (subscription.effective_status === 'expired') return true;
    if (subscription.days_remaining !== undefined) return subscription.days_remaining < 0;
    if (!subscription.end_date) return false;
    const part = String(subscription.end_date).slice(0, 10);
    const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
    if (!m) return false;
    const todayStr = new Date().toLocaleDateString('en-CA');
    return `${m[1]}-${m[2]}-${m[3]}` < todayStr;
}

function InsuranceModal({ clientId, initial, onClose, onSuccess }) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const [formData, setFormData] = useState({
        policy_number: initial?.policy_number ?? '',
        company: initial?.company ?? '',
        expiry_date: initial?.expiry_date ?? '',
        notes: initial?.notes ?? '',
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (initial) {
            setFormData({
                policy_number: initial.policy_number ?? '',
                company: initial.company ?? '',
                expiry_date: initial.expiry_date ?? '',
                notes: initial.notes ?? '',
            });
        } else {
            setFormData({ policy_number: '', company: '', expiry_date: '', notes: '' });
        }
    }, [initial]);

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!formData.expiry_date) {
            setError('Data di scadenza obbligatoria');
            return;
        }
        setLoading(true);
        setError(null);
        try {
            const api = getAuthenticatedApi();
            await api.post('/client_insurance_management.php?action=save_insurance', {
                client_id: clientId,
                id: initial?.id ?? undefined,
                policy_number: formData.policy_number || null,
                company: formData.company || null,
                expiry_date: formData.expiry_date,
                notes: formData.notes || null,
            });
            toast.success(initial ? 'Assicurazione aggiornata' : 'Assicurazione registrata');
            onSuccess();
        } catch (err) {
            setError(err.response?.data?.error || 'Errore salvataggio');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
            <div className="flex w-full max-w-md flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl dark:border-white/10 dark:bg-gray-900">
                <div className="border-b border-gray-200 px-5 py-3 dark:border-white/10">
                    <h3 className="text-base font-semibold text-gray-900 dark:text-white">
                        {initial ? t('gym.membersManager.insurance.modalTitleEdit') : t('gym.membersManager.insurance.modalTitleAdd')}
                    </h3>
                </div>
                <form onSubmit={handleSubmit} className="space-y-3 px-5 py-4">
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{t('gym.membersManager.insurance.expiry')} *</label>
                        <input type="date" required value={formData.expiry_date} onChange={(e) => setFormData((p) => ({ ...p, expiry_date: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{t('gym.membersManager.insurance.policyNumber')}</label>
                        <input type="text" value={formData.policy_number} onChange={(e) => setFormData((p) => ({ ...p, policy_number: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{t('gym.membersManager.insurance.company')}</label>
                        <input type="text" value={formData.company} onChange={(e) => setFormData((p) => ({ ...p, company: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                    </div>
                    <div>
                        <label className="mb-1 block text-xs font-medium text-gray-700 dark:text-gray-300">{t('gym.membersManager.insurance.notesLabel')}</label>
                        <textarea rows={2} value={formData.notes} onChange={(e) => setFormData((p) => ({ ...p, notes: e.target.value }))} className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800 dark:text-white" />
                    </div>
                    {error && <p className="text-xs text-red-600 dark:text-red-400">{error}</p>}
                    <div className="flex justify-end gap-2 border-t border-gray-200 pt-3 dark:border-white/10">
                        <button type="button" onClick={onClose} className="rounded-lg px-3 py-1.5 text-sm text-gray-700 dark:text-white/80">Annulla</button>
                        <button type="submit" disabled={loading} className="rounded-lg bg-indigo-600 px-3 py-1.5 text-sm font-medium text-white disabled:opacity-50">
                            {loading ? '...' : t('gym.membersManager.insurance.save')}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

export default function MemberSubscriptionTab({
    member,
    subscriptionRefreshKey = 0,
    onOpenSubscriptionModal,
    canModify,
}) {
    const { getAuthenticatedApi } = useAuth();
    const { t } = useTranslation();
    const isMobile = useMediaQuery('(max-width: 1023px)');
    const [subscription, setSubscription] = useState(null);
    const [subscriptionHistory, setSubscriptionHistory] = useState([]);
    const [insurance, setInsurance] = useState(null);
    const [insuranceHistory, setInsuranceHistory] = useState([]);
    const [showInsuranceModal, setShowInsuranceModal] = useState(false);
    const [insuranceToEdit, setInsuranceToEdit] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchSubscriptionData = useCallback(async () => {
        const memberId = member.id;
        setSubscription(null);
        setSubscriptionHistory([]);
        setInsurance(null);
        setInsuranceHistory([]);
        setError(null);
        setLoading(true);
        try {
            const api = getAuthenticatedApi();
            try {
                const currentResponse = await api.get(`/client_subscription_management.php?action=client_subscription&client_id=${memberId}`);
                if (member.id !== memberId) return;
                if (currentResponse.data.success) setSubscription(currentResponse.data.subscription);
            } catch (err) {
                if (member.id !== memberId) return;
                if (err.response?.status !== 403) throw err;
            }
            try {
                const historyResponse = await api.get(`/client_subscription_management.php?action=client_subscription_history&client_id=${memberId}`);
                if (member.id !== memberId) return;
                if (historyResponse.data.success) setSubscriptionHistory(historyResponse.data.history);
            } catch (err) {
                if (member.id !== memberId) return;
                if (err.response?.status !== 403) throw err;
            }
            try {
                const insResponse = await api.get(`/client_insurance_management.php?action=client_insurance&client_id=${memberId}`);
                if (member.id !== memberId) return;
                if (insResponse.data.success) setInsurance(insResponse.data.insurance);
            } catch (err) {
                if (member.id !== memberId) return;
                if (err.response?.status !== 403) console.error(err);
            }
            try {
                const insHistResponse = await api.get(`/client_insurance_management.php?action=client_insurance_history&client_id=${memberId}`);
                if (member.id !== memberId) return;
                if (insHistResponse.data.success) setInsuranceHistory(insHistResponse.data.history || []);
            } catch (err) {
                if (member.id !== memberId) return;
                if (err.response?.status !== 403) console.error(err);
            }
        } catch (err) {
            console.error('Errore caricamento abbonamento:', err);
            setError(t('gym.membersManager.subscription.loadError'));
        } finally {
            setLoading(false);
        }
    }, [getAuthenticatedApi, member.id, t]);

    useEffect(() => {
        fetchSubscriptionData();
    }, [fetchSubscriptionData, subscriptionRefreshKey]);

    const formatPrice = (price, type) => {
        if (price === null || price === undefined || Number(price) === 0) return '-';
        const suffix = type === 'monthly' ? `/${t('gym.membersManager.subscription.month')}` : type === 'yearly' ? `/${t('gym.membersManager.subscription.year')}` : '';
        return `€${Number(price).toLocaleString('it-IT', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}${suffix}`;
    };

    const formatSubscriptionDateIt = (isoDate) => {
        if (!isoDate) return '-';
        const part = String(isoDate).trim().split('T')[0];
        const matched = /^(\d{4})-(\d{2})-(\d{2})$/.exec(part);
        if (matched) return `${matched[3]}/${matched[2]}/${matched[1]}`;
        try {
            return new Date(isoDate).toLocaleDateString('it-IT');
        } catch {
            return part;
        }
    };

    if (loading) {
        return (
            <div className="flex justify-center py-8">
                <LoadingSpinner />
            </div>
        );
    }

    if (error) {
        return (
            <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950/30 dark:text-red-300">
                {error}
            </div>
        );
    }

    const subExpired = isSubscriptionExpired(subscription);
    const pastInsurance = insuranceHistory.filter((h) => h.id !== insurance?.id);
    const pastSubscriptions = subscriptionHistory.filter((s) => s.id !== subscription?.id);

    const insuranceStatusLabel = insurance?.status === 'valid'
        ? t('gym.membersManager.insurance.statusValid')
        : insurance?.status === 'expiring_soon'
            ? t('gym.membersManager.insurance.statusExpiring')
            : t('gym.membersManager.insurance.statusExpired');

    return (
        <div className="space-y-3 pb-2">
            {canModify && (
                <div className="flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        onClick={(e) => onOpenSubscriptionModal('create', null, e)}
                        className="inline-flex items-center gap-1.5 rounded-lg bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-indigo-700 sm:text-sm"
                    >
                        <CreditCard size={14} aria-hidden />
                        {t('gym.membersManager.subscription.newSubscription')}
                    </button>
                </div>
            )}

            <div className={`grid gap-3 ${isMobile ? 'grid-cols-1' : 'grid-cols-1 lg:grid-cols-2'}`}>
                <Panel
                    title={t('gym.membersManager.subscription.currentSubscription')}
                    icon={CreditCard}
                    action={subscription && canModify ? (
                        <button
                            type="button"
                            onClick={(e) => onOpenSubscriptionModal('edit', subscription, e)}
                            className="text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                            {t('gym.membersManager.subscription.edit')}
                        </button>
                    ) : null}
                >
                    {subscription ? (
                        <>
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${!subExpired ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200' : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'}`}>
                                    {!subExpired ? t('gym.membersManager.memberStatus.active') : t('gym.membersManager.subscription.subscriptionExpiredLabel')}
                                </span>
                                {subscription.days_remaining > 0 && (
                                    <span className="text-xs text-gray-500 dark:text-white/60">
                                        {subscription.days_remaining} {t('gym.membersManager.subscription.days')}
                                    </span>
                                )}
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                <CompactStat label={t('gym.membersManager.subscription.type')} value={subscription.subscription_name} />
                                <CompactStat label={t('gym.membersManager.subscription.expiration')} value={formatSubscriptionDateIt(subscription.end_date)} />
                                <CompactStat label={t('gym.membersManager.subscription.price')} value={formatPrice(subscription.price, subscription.subscription_type)} />
                                <CompactStat
                                    label={t('gym.membersManager.subscription.paymentStatus')}
                                    value={subscription.payment_status === 'paid' ? t('gym.membersManager.subscription.paid') : t('gym.membersManager.subscription.pending')}
                                    valueClassName={subscription.payment_status === 'paid' ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}
                                />
                            </div>
                            {subscription.notes?.trim() && (
                                <p className="mt-2 rounded-lg bg-gray-50 px-2 py-1.5 text-xs text-gray-600 dark:bg-gray-800/60 dark:text-white/75">
                                    <span className="font-medium">{t('gym.membersManager.subscription.notesLabel')}: </span>
                                    {subscription.notes}
                                </p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-amber-800 dark:text-amber-200/90">{t('gym.membersManager.subscription.noActiveSubscription')}</p>
                    )}
                </Panel>

                <Panel
                    title={t('gym.membersManager.insurance.title')}
                    icon={ShieldCheck}
                    alert={insurance?.status === 'expired' ? t('gym.membersManager.insurance.warningExpired') : null}
                    action={canModify ? (
                        <button
                            type="button"
                            onClick={() => { setInsuranceToEdit(insurance || null); setShowInsuranceModal(true); }}
                            className="inline-flex items-center gap-1 text-xs font-medium text-indigo-600 hover:underline dark:text-indigo-300"
                        >
                            <Plus size={12} aria-hidden />
                            {insurance ? t('gym.membersManager.insurance.edit') : t('gym.membersManager.insurance.add')}
                        </button>
                    ) : null}
                >
                    {insurance ? (
                        <>
                            <div className="mb-2 flex flex-wrap items-center gap-2">
                                <span className={`rounded-full px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide ${
                                    insurance.status === 'valid' ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/40 dark:text-emerald-200'
                                        : insurance.status === 'expiring_soon' ? 'bg-amber-100 text-amber-900 dark:bg-amber-900/40 dark:text-amber-100'
                                            : 'bg-red-100 text-red-800 dark:bg-red-900/40 dark:text-red-200'
                                }`}>
                                    {insuranceStatusLabel}
                                </span>
                                <span className="text-xs text-gray-500 dark:text-white/60">
                                    {t('gym.membersManager.insurance.expiry')}: {new Date(insurance.expiry_date).toLocaleDateString('it-IT')}
                                </span>
                            </div>
                            <div className="grid grid-cols-2 gap-2">
                                {insurance.policy_number && <CompactStat label={t('gym.membersManager.insurance.policyNumber')} value={insurance.policy_number} />}
                                {insurance.company && <CompactStat label={t('gym.membersManager.insurance.company')} value={insurance.company} />}
                            </div>
                            {insurance.notes?.trim() && (
                                <p className="mt-2 text-xs text-gray-600 dark:text-white/75">{insurance.notes}</p>
                            )}
                        </>
                    ) : (
                        <p className="text-sm text-gray-500 dark:text-white/60">{t('gym.membersManager.insurance.noInsurance')}</p>
                    )}
                </Panel>
            </div>

            <CollapsibleHistory title={t('gym.membersManager.subscription.subscriptionHistory')} count={pastSubscriptions.length}>
                {pastSubscriptions.map((sub) => (
                    <div key={sub.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2 text-sm">
                        <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900 dark:text-white">{sub.subscription_name}</p>
                            <p className="text-xs text-gray-500 dark:text-white/55">
                                {formatSubscriptionDateIt(sub.start_date)} - {formatSubscriptionDateIt(sub.end_date)}
                                {' · '}
                                {formatPrice(sub.price, sub.subscription_type)}
                            </p>
                        </div>
                        <span className="shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase bg-gray-100 text-gray-700 dark:bg-gray-800 dark:text-white/80">
                            {(sub.effective_status || sub.status) === 'active'
                                ? t('gym.membersManager.memberStatus.active')
                                : t('gym.membersManager.subscription.subscriptionExpiredLabel')}
                        </span>
                    </div>
                ))}
            </CollapsibleHistory>

            <CollapsibleHistory title={t('gym.membersManager.insurance.history')} count={pastInsurance.length}>
                {pastInsurance.map((h) => (
                    <div key={h.id} className="px-3 py-2 text-sm text-gray-700 dark:text-white/85">
                        <span className="text-gray-500 dark:text-white/55">{new Date(h.expiry_date).toLocaleDateString('it-IT')}</span>
                        {h.company && <span className="ml-2 font-medium">{h.company}</span>}
                        {h.policy_number && <span className="ml-1 text-xs text-gray-400">({h.policy_number})</span>}
                    </div>
                ))}
            </CollapsibleHistory>

            {showInsuranceModal && (
                <InsuranceModal
                    clientId={member.id}
                    initial={insuranceToEdit}
                    onClose={() => { setShowInsuranceModal(false); setInsuranceToEdit(null); }}
                    onSuccess={() => { setShowInsuranceModal(false); setInsuranceToEdit(null); fetchSubscriptionData(); }}
                />
            )}
        </div>
    );
}
